import { getAppointmentDepositSummary } from "../../../helpers/appointmentDetail";

const positiveAmount = (value) => {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") return 0;
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const moneyAmount = (value) => {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
};

export const totalDepositFromServiceDeposits = (serviceDeposits, fallback = 0) => {
  const configuredAmounts = (Array.isArray(serviceDeposits) ? serviceDeposits : [])
    .map((service) => moneyAmount(service?.deposit_price))
    .filter((amount) => amount !== null);
  if (configuredAmounts.length > 0) {
    const totalMinor = configuredAmounts.reduce((sum, amount) => sum + Math.round(amount * 100), 0);
    return (totalMinor / 100).toFixed(2);
  }

  return (moneyAmount(fallback) ?? 0).toFixed(2);
};

export const getAppointmentDepositFormState = (detail = {}) => {
  const configuredDeposit = [
    detail?.deposit,
    detail?.totaldeposit,
    detail?.total_deposit,
    detail?.service?.deposit_booked_price,
  ].map(moneyAmount).find((amount) => amount !== null) ?? 0;
  const { depositBalance: previousDepositPaid } = getAppointmentDepositSummary(detail);

  return {
    configuredDeposit: configuredDeposit.toFixed(2),
    previousDepositPaid: previousDepositPaid.toFixed(2),
    remainingDeposit: Math.max(0, configuredDeposit - previousDepositPaid).toFixed(2),
  };
};

export const hasStripeDeposit = (values = {}) =>
  positiveAmount(values.totaldeposit) > 0 &&
  values.deposit_payment_by === "StripeCreditCard";

export const hasAppointmentDepositSelection = (values = {}) =>
  positiveAmount(values.totaldeposit) > 0 &&
  ["BankTransfer", "Cash", "CreditCard", "StripeCreditCard", "Voucher"].includes(values.deposit_payment_by);

export const buildAppointmentDepositPayload = (values = {}, idempotencyKey = "") => ({
  appointment_id: values.id,
  client_id: values.client_id,
  totaldeposit: values.totaldeposit,
  deposit_payment_by: values.deposit_payment_by,
  ...(values.deposit_payment_by === "Voucher" && values.voucher_to_id
    ? { voucher_to_id: values.voucher_to_id }
    : {}),
  idempotency_key: idempotencyKey,
});

export const paymentMethodConfirmationLabel = (paymentMethod) => {
  if (paymentMethod === "Cash") return "via cash";
  if (paymentMethod === "BankTransfer") return "via bank transfer";
  if (paymentMethod === "CreditCard") return "via credit card";
  if (paymentMethod === "Voucher") return "via voucher";
  return "";
};

export const voucherRemainingAmount = (voucher = {}) => {
  const minor = Number(voucher?.remaining_balance?.amount_minor);
  return Number.isFinite(minor) ? minor / 100 : null;
};

export const eligibleAppointmentVouchers = (vouchers = []) =>
  (Array.isArray(vouchers) ? vouchers : []).filter((voucher) => {
    if (!voucher || voucher.is_active === false) return false;
    const remaining = voucherRemainingAmount(voucher);
    return remaining !== null && remaining > 0;
  });

export const findEligibleAppointmentVoucherByCode = (vouchers, code) => {
  const normalized = String(code || "").trim().toUpperCase();
  if (!normalized) return null;
  return eligibleAppointmentVouchers(vouchers).find(
    (voucher) => String(voucher.code || "").trim().toUpperCase() === normalized,
  ) || null;
};

export const appointmentVoucherLabel = (voucher = {}) => {
  const remaining = voucherRemainingAmount(voucher);
  const currency = voucher?.remaining_balance?.currency || "";
  const amount = remaining === null ? "" : remaining.toFixed(2);
  return [voucher?.code, [currency, amount].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(" · ");
};

export const appointmentPaymentIntent = (response = {}) => ({
  id: response.payment_intent_id || response.paymentIntentId || response.payment_intent?.id || "",
  clientSecret: response.client_secret || response.clientSecret || response.payment_intent?.client_secret || "",
});

export const stripeConfirmation = (response, paymentMethodId) => {
  const intent = appointmentPaymentIntent(response);
  if (!intent.clientSecret || !paymentMethodId) return null;
  return {
    clientSecret: intent.clientSecret,
    payment_method: paymentMethodId,
  };
};

// Match a selected occurrence, since a primary and multiple child lines can
// legitimately use the same service. Keep a manually lowered quote lowered.
export const removeAppointmentServiceDeposit = (values, field, index) => {
  const children = values[field] || [];
  const serviceId = children[index]?.service_id;
  const type = field === "addonservices" ? "add_on" : "add_another";
  let occurrence = children.slice(0, index).filter(line => String(line.service_id) === String(serviceId)).length;
  const deposits = Array.isArray(values.service_deposit) ? values.service_deposit : [];
  const removedIndex = deposits.findIndex(line => line.service_type === type
    && String(line.service_id) === String(serviceId) && occurrence-- === 0);
  const removedMinor = removedIndex < 0 ? 0 : Math.round(positiveAmount(deposits[removedIndex].deposit_price) * 100);
  const totalMinor = Math.round(positiveAmount(values.totaldeposit) * 100);
  return {
    service_deposit: deposits.filter((_, index) => index !== removedIndex),
    totaldeposit: (Math.max(0, totalMinor - removedMinor) / 100).toFixed(2),
  };
};

// Remove only deleted selections from the editable quote, retaining a lowered quote.
const removeQuotedServiceDeposits = (values, keep) => {
  const deposits = Array.isArray(values.service_deposit) ? values.service_deposit : [];
  const removedMinor = deposits.filter(line => !keep(line))
    .reduce((sum, line) => sum + Math.round(positiveAmount(line.deposit_price) * 100), 0);
  return {
    service_deposit: deposits.filter(keep),
    totaldeposit: (Math.max(0, Math.round(positiveAmount(values.totaldeposit) * 100) - removedMinor) / 100).toFixed(2),
  };
};

export const clearAppointmentPrimaryDeposit = (values = {}) =>
  removeQuotedServiceDeposits(values, line => line.service_type === "add_another");

export const removeAppointmentAddonDeposit = (values = {}) => {
  if (!Array.isArray(values.service_deposit) || values.service_deposit.length === 0) {
    return {service_deposit: [], totaldeposit: values.totaldeposit ?? 0};
  }
  return removeQuotedServiceDeposits(values, line => line.service_type !== "add_on");
};
